import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, createSelector } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IdName } from 'src/app/shared/models/id-name';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ROOT_MAINTENANCE_COMPONENT } from '../constants';
import { ModuleNumbers } from '../enums/module-numbers.enum';
import { MenuItemModel, MenuItemWithIndent } from '../models/menu-id.model';
import { CreateUpdateDeleteRecursiveArrayService } from '../services/create-update-delete-recursive-array.service';
import { MenuListLogicService } from '../services/menu-list-logic.service';
import { MenuTreeStructureApiService } from '../services/menu-tree-structure-api.service';
import {
  CopyMenuStructure,
  CreateMenuStructure,
  DeleteMenuStructure,
  GetMenuStructureElementById,
  ClearDocumentStructure,
  LoadDocumentMenuRootStructures,
  LoadDocumentMenuStructure,
  LoadFlatMenuStructureByDepartmentAndModule,
  LoadMenuStructure,
  LoadSpecificMenuStructure,
  LoadSpecificMenuStructureForList,
  LoadSpecificMenuStructureForTenant,
  MainMenuChange,
  ManageAccessAndResponsibleInComponents,
  SaveMenuStructureIdNameList,
  SelectMenuStructureNode,
  SetActiveMenuElement,
  UpdateMenuStructure,
  UpdateMenuStructureOrder,
  OpenMenuItem,
  CloseMenuItem,
  ClearMenuSelection,
  RestoreMenuSelection,
} from './menu-tree-structure.actions';
import { IMenuPlacement } from '../models/menu-placement.model';

export interface MenuTreeStructureStateModel {
  loaded: boolean;
  menuStructure: MenuItemModel[];
  menuStructureIdName: IdName[];
  menuStructureIndentedIdName: MenuItemWithIndent[];
  menuStructureIndentedIdNameForList: MenuItemWithIndent[];
  menuStructureDetailed: MenuItemModel | undefined;
  currentTableData: any[];
  displayName: string;
  documentParentId: string;
  currentModule: ModuleNumbers;
  displayedTreeModule: ModuleNumbers | null;
  activeMenuElementId: string;
  menuFlatStructureByDepartamentAndModule: IMenuPlacement[];
  currentMenuOption: string;
  openedMenuItemIdsByModule: { [module: number]: string[] };
}

const MENU_TREE_STRUCTURE_STATE_TOKEN = new StateToken<MenuTreeStructureStateModel>(
  'menuTreeStructure'
);

@State<MenuTreeStructureStateModel>({
  name: MENU_TREE_STRUCTURE_STATE_TOKEN,
  defaults: {
    loaded: false,
    menuStructure: [],
    menuStructureIdName: [ROOT_MAINTENANCE_COMPONENT],
    menuStructureIndentedIdName: [ROOT_MAINTENANCE_COMPONENT],
    menuStructureIndentedIdNameForList: [],
    menuStructureDetailed: undefined,
    currentTableData: [],
    displayName: '',
    documentParentId: '',
    currentModule: 0,
    displayedTreeModule: null,
    activeMenuElementId: '',
    menuFlatStructureByDepartamentAndModule: [],
    currentMenuOption: '',
    openedMenuItemIdsByModule: {},
  },
})
@Injectable()
export class MenuTreeStructureState {
  constructor(
    private menuListLogicService: MenuListLogicService,
    private service: MenuTreeStructureApiService,
    private createUpdateDeleteRecursiveArrayService: CreateUpdateDeleteRecursiveArrayService
  ) {}

  @Action(LoadMenuStructure, { cancelUncompleted: true })
  loadMenuStructure({ getState, setState }: StateContext<MenuTreeStructureStateModel>) {
    setState(patch({ loaded: false }));
    return this.service.getEntireMenuTreeStructure().pipe(
      tap((menuStructure: MenuItemModel[]) => {
        const state = getState();
        setState({
          ...state,
          loaded: true,
          menuStructure,
        });
      })
    );
  }

  // Action below is called only from menu-list-recursive
  // whenever a menu option is chosen, to clear previously uncleared
  // variable called currentTableData
  @Action(MainMenuChange, { cancelUncompleted: true })
  mainMenuChange(
    { getState, setState }: StateContext<MenuTreeStructureStateModel>,
    { menuOption }: MainMenuChange
  ) {
    const state = getState();
    if (menuOption !== getState().currentMenuOption) {
      setState({
        ...state,
        currentMenuOption: menuOption,
        currentTableData: [],
      });
    }
  }

  @Action(LoadSpecificMenuStructure, { cancelUncompleted: true })
  loadSpecificMenuStructure(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { module, displayName, nodeId }: LoadSpecificMenuStructure
  ) {
    ctx.setState(patch({ loaded: false }));
    return this.service
      .getSpecificMenuTreeStructure(module)
      .pipe(tap(this.onSpecificMenuLoad(ctx, displayName, nodeId, module)));
  }

  @Action(LoadSpecificMenuStructureForTenant, { cancelUncompleted: true })
  loadSpecificMenuStructureForTenant(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { module, displayName, nodeId }: LoadSpecificMenuStructureForTenant
  ) {
    ctx.setState(patch({ loaded: false }));
    return this.service
      .getSpecificMenuTreeStructure(module, true)
      .pipe(tap(this.onSpecificMenuLoad(ctx, displayName, nodeId, module)));
  }

  @Action(LoadSpecificMenuStructureForList, { cancelUncompleted: true })
  loadSpecificMenuStructureForList(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { module }: LoadSpecificMenuStructureForList
  ) {
    return this.service.getSpecificMenuTreeStructure(module).pipe(
      tap((menuStructureIndentedIdNameForList) => {
        ctx.setState(
          patch({
            menuStructureIndentedIdNameForList:
              this.menuListLogicService.getIdNameIndentListOfPossibleLocations(
                menuStructureIndentedIdNameForList
              ),
          })
        );
      })
    );
  }

  @Action(LoadDocumentMenuRootStructures, { cancelUncompleted: true })
  loadDocumentMenuRootStructures(ctx: StateContext<MenuTreeStructureStateModel>) {
    ctx.setState(patch({ loaded: false }));
    return this.service.getDocumentMenuRootStructure().pipe(
      tap((documentMenuRootStructures: MenuItemModel[]) => {
        ctx.setState(
          patch({
            menuStructure: documentMenuRootStructures,
            loaded: true,
            currentModule: ModuleNumbers.Document,
            displayedTreeModule: ModuleNumbers.Document,
          })
        );
      })
    );
  }

  @Action(LoadDocumentMenuStructure, { cancelUncompleted: true })
  loadDocumentMenuStructure(ctx: StateContext<MenuTreeStructureStateModel>) {
    ctx.setState(patch({ loaded: false }));
    return this.service.getMenuByDepartment(ModuleNumbers.Document).pipe(
      tap((documentMenuStructureElement: MenuItemModel[]) => {
        ctx.setState(
          patch({
            menuStructure: documentMenuStructureElement,
            loaded: true,
            currentModule: ModuleNumbers.Document,
            displayedTreeModule: ModuleNumbers.Document,
          })
        );
        ctx.dispatch(new SaveMenuStructureIdNameList(documentMenuStructureElement));
      })
    );
  }

  @Action(SaveMenuStructureIdNameList, { cancelUncompleted: true })
  saveMenuStructureIdNameList(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { structure }: SaveMenuStructureIdNameList
  ) {
    const list = this.menuListLogicService.getIdNameFlatListOfPossibleLocations(structure);
    const indentedList =
      this.menuListLogicService.getIdNameIndentListOfPossibleLocations(structure);
    list.unshift(ROOT_MAINTENANCE_COMPONENT);
    indentedList.unshift(ROOT_MAINTENANCE_COMPONENT);

    ctx.setState(
      patch({
        menuStructureIdName: list,
        menuStructureIndentedIdName: indentedList,
      })
    );
  }

  @Action(GetMenuStructureElementById, { cancelUncompleted: true })
  getMenuStructureElementById(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { id }: GetMenuStructureElementById
  ) {
    return this.service.getMenuStructureElementById(id).pipe(
      tap((menuStructureDetailed: MenuItemModel[]) => {
        ctx.setState(
          patch({
            menuStructureDetailed: menuStructureDetailed[0],
            menuStructure: menuStructureDetailed[0].children,
            documentParentId: menuStructureDetailed[0].id,
            currentModule: ModuleNumbers.Document,
            displayedTreeModule: ModuleNumbers.Document,
          })
        );
        ctx.dispatch(new SaveMenuStructureIdNameList(menuStructureDetailed));
      })
    );
  }

  @Action(ClearDocumentStructure)
  clearDocumentStructure(ctx: StateContext<MenuTreeStructureStateModel>) {
    ctx.setState(
      patch({
        menuStructureDetailed: undefined,
      })
    );
  }

  @Action(LoadFlatMenuStructureByDepartmentAndModule, { cancelUncompleted: true })
  loadFlatMenuStructureByDepartmentAndModule(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { departmentId, module }: LoadFlatMenuStructureByDepartmentAndModule
  ) {
    return this.service.getFlatMenuStructureByDepartmentAndModule(departmentId, module).pipe(
      tap((flatMenuStructure: IMenuPlacement[]) => {
        ctx.setState(
          patch({
            menuFlatStructureByDepartamentAndModule: flatMenuStructure,
          })
        );
      })
    );
  }

  @Action(CreateMenuStructure, { cancelUncompleted: true })
  createMenuStructure(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { menuStructure, mainRoot }: CreateMenuStructure
  ) {
    if (
      !mainRoot &&
      menuStructure.module === ModuleNumbers.Document &&
      menuStructure.parentId &&
      menuStructure.parentId.length < 5
    ) {
      menuStructure.parentId = ctx.getState().documentParentId;
    }
    return this.service.createMenuStructure(menuStructure).pipe(
      tap((menuTreeId: string) => {
        if (mainRoot && menuStructure.module === ModuleNumbers.Document) {
          ctx.dispatch(new LoadDocumentMenuRootStructures());
          return of();
        }
        if (!mainRoot && menuStructure.module === ModuleNumbers.Document) {
          ctx.dispatch(new GetMenuStructureElementById(ctx.getState().documentParentId));
          return of();
        }
        ctx.dispatch(new LoadSpecificMenuStructure(menuStructure.module));
        return of();
        // const newMenuTree: MenuItemModel = {
        //   id: menuTreeId,
        //   parentId: menuStructure.parentId,
        //   code: menuStructure.code,
        //   name: menuStructure.name,
        //   module: menuStructure.module,
        //   displayName: menuStructure.displayName,
        //   departments: menuStructure.departments,
        //   icon: menuStructure.icon,
        //   count: 0,
        //   sortNumber: 0,
        //   children: [],
        // };
        // if (!newMenuTree.parentId) {
        //   ctx.setState(
        //     patch({
        //       menuStructure: append([newMenuTree]),
        //     })
        //   );
        //   return;
        // }

        // const state = ctx.getState();
        // const updatedItems =
        //   this.createUpdateDeleteRecursiveArrayService.addNewItemToParentChildrenElements(
        //     state.menuStructure,
        //     menuStructure.parentId,
        //     newMenuTree
        //   );

        // ctx.setState(
        //   patch({
        //     menuStructure: updatedItems,
        //   })
        // );
      })
    );
  }

  @Action(UpdateMenuStructure, { cancelUncompleted: true })
  updateMenuStructure(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { menuStructure, mainRoot }: UpdateMenuStructure
  ) {
    return this.service.updateMenuStructure(menuStructure).pipe(
      tap((menuTreeId: string) => {
        if (mainRoot && menuStructure.module === ModuleNumbers.Document) {
          ctx.dispatch(new LoadDocumentMenuRootStructures());
          return of();
        }
        if (!mainRoot && menuStructure.module === ModuleNumbers.Document) {
          ctx.dispatch(new GetMenuStructureElementById(ctx.getState().documentParentId));
          return of();
        }
        ctx.dispatch(new LoadSpecificMenuStructure(menuStructure.module));
        return of();
        // const updatedMenuTree: MenuItemModel = {
        //   id: menuTreeId,
        //   parentId: menuStructure.parentId,
        //   code: menuStructure.code,
        //   name: menuStructure.name,
        //   module: menuStructure.module,
        //   displayName: menuStructure.displayName,
        //   departments: menuStructure.departments,
        //   icon: menuStructure.icon,
        //   count: 0,
        //   sortNumber: 0,
        //   children: menuStructure.children,
        // };

        // const state = ctx.getState();
        // const updatedItems =
        //   this.createUpdateDeleteRecursiveArrayService.updateItemInParentChildrenElements(
        //     state.menuStructure,
        //     menuStructure.parentId,
        //     updatedMenuTree
        //   );

        // ctx.setState(
        //   patch({
        //     menuStructure: updatedItems,
        //   })
        // );
      })
    );
  }

  @Action(DeleteMenuStructure, { cancelUncompleted: true })
  deleteMenuStructure(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { id, mainRoot }: DeleteMenuStructure
  ) {
    return this.service.deleteMenuStructure(id).pipe(
      tap(() => {
        const { currentModule } = ctx.getState();
        if (mainRoot && currentModule === ModuleNumbers.Document) {
          ctx.dispatch(new LoadDocumentMenuRootStructures());
          return of();
        }
        if (!mainRoot && currentModule === ModuleNumbers.Document) {
          ctx.dispatch(new GetMenuStructureElementById(ctx.getState().documentParentId));
          return of();
        }
        ctx.dispatch(new LoadSpecificMenuStructure(currentModule));
        return of();
        // const state = ctx.getState();
        // const updatedItems = this.createUpdateDeleteRecursiveArrayService.removeItemFromNestedItems(
        //   state.menuStructure,
        //   id
        // );

        // ctx.setState(
        //   patch({
        //     menuStructure: updatedItems,
        //   })
        // );
      })
    );
  }

  @Action(SetActiveMenuElement, { cancelUncompleted: true })
  setActiveMenuElement(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { id }: SetActiveMenuElement
  ) {
    ctx.setState(patch({ activeMenuElementId: id }));
  }

  @Action(SelectMenuStructureNode)
  selectMenuStructureNode(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { nodeId, displayName }: SelectMenuStructureNode
  ) {
    const selectedNode =
      this.createUpdateDeleteRecursiveArrayService.getNodeWithChildrenOneLevelDeep(
        ctx.getState().menuStructure,
        nodeId
      );
    ctx.setState(
      patch({
        currentTableData: [selectedNode],
        displayName,
        activeMenuElementId: nodeId,
      })
    );
  }

  @Action(ClearMenuSelection)
  clearMenuSelection(ctx: StateContext<MenuTreeStructureStateModel>) {
    ctx.setState(
      patch({
        activeMenuElementId: '',
        currentTableData: [],
        displayName: '',
      })
    );
  }

  @Action(RestoreMenuSelection)
  restoreMenuSelection(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { activeMenuElementId, currentTableData, displayName }: RestoreMenuSelection
  ) {
    ctx.setState(patch({ activeMenuElementId, currentTableData, displayName }));
  }

  @Action(CopyMenuStructure, { cancelUncompleted: true })
  @OnSuccess({ message: 'copyingMenuStartedToast' })
  copyMenuStructure(ctx: StateContext<MenuTreeStructureStateModel>, { model }: CopyMenuStructure) {
    return this.service.copyMenuStructure(model);
  }

  @Action(ManageAccessAndResponsibleInComponents, { cancelUncompleted: true })
  @OnSuccess({ message: 'accessAndOrResponsibleAssigned' })
  manageAccessAndResponsibleInComponents(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { model }: ManageAccessAndResponsibleInComponents
  ) {
    return this.service.manageAccessAndResponsibleInComponents(model);
  }

  @Action(UpdateMenuStructureOrder, { cancelUncompleted: true })
  @OnSuccess({ message: 'menuStructureOrderUpdated' })
  updateMenuStructureOrder(
    ctx: StateContext<MenuTreeStructureStateModel>,
    { order, mainRoot }: UpdateMenuStructureOrder
  ) {
    return this.service.updateMenuStructureOrder(order);
  }

  @Action(OpenMenuItem)
  openMenuItem(ctx: StateContext<MenuTreeStructureStateModel>, { module, id }: OpenMenuItem) {
    const state = ctx.getState();
    const ids = state.openedMenuItemIdsByModule[module] || [];
    if (!ids.includes(id)) {
      ctx.setState({
        ...state,
        openedMenuItemIdsByModule: {
          ...state.openedMenuItemIdsByModule,
          [module]: [...ids, id],
        },
      });
    }
  }

  @Action(CloseMenuItem)
  closeMenuItem(ctx: StateContext<MenuTreeStructureStateModel>, { module, id }: CloseMenuItem) {
    const state = ctx.getState();
    const ids = state.openedMenuItemIdsByModule[module] || [];
    if (ids.includes(id)) {
      ctx.setState({
        ...state,
        openedMenuItemIdsByModule: {
          ...state.openedMenuItemIdsByModule,
          [module]: ids.filter((openedId) => openedId !== id),
        },
      });
    }
  }

  @Selector()
  static menuStructureLoaded(state: MenuTreeStructureStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getEntireMenuStructure(state: MenuTreeStructureStateModel) {
    return state.menuStructure;
  }

  @Selector()
  static getIndentedMenuStructureForList(state: MenuTreeStructureStateModel) {
    return state.menuStructureIndentedIdNameForList;
  }

  @Selector()
  static getSelectedPartOfMenuStructure(state: MenuTreeStructureStateModel) {
    return state.currentTableData;
  }

  @Selector()
  static getCurrentModuleNumber(state: MenuTreeStructureStateModel): number {
    return state.currentModule;
  }

  @Selector()
  static getDisplayedTreeModule(state: MenuTreeStructureStateModel): ModuleNumbers | null {
    return state.displayedTreeModule;
  }

  @Selector()
  static getMenuStructureIdNameList(state: MenuTreeStructureStateModel) {
    return state.menuStructureIdName;
  }

  @Selector()
  static getMenuStructureIndentedIdNameList(state: MenuTreeStructureStateModel) {
    return state.menuStructureIndentedIdName;
  }

  @Selector()
  static getMenuFlatStructureByDepartamentAndModule(state: MenuTreeStructureStateModel) {
    return state.menuFlatStructureByDepartamentAndModule;
  }

  @Selector()
  static getDetailedMenuStructureElement(state: MenuTreeStructureStateModel) {
    return state.menuStructureDetailed;
  }

  @Selector()
  static getSelectedMenuId(state: MenuTreeStructureStateModel) {
    return state.currentTableData?.[0]?.id;
  }

  @Selector()
  static getDisplayName(state: MenuTreeStructureStateModel) {
    return state.displayName;
  }

  @Selector()
  static activeMenuElementId(state: MenuTreeStructureStateModel) {
    return state.activeMenuElementId;
  }

  @Selector()
  static getActiveMenuElementName(state: MenuTreeStructureStateModel) {
    return state.menuStructureIdName.find((item) => item.id === state.activeMenuElementId)?.name;
  }

  static getMenuItemById(id: string) {
    return createSelector([MenuTreeStructureState], (state: MenuTreeStructureStateModel) => {
      return CreateUpdateDeleteRecursiveArrayService.getItemThatMatchParentId(
        state.menuStructure,
        id
      );
    });
  }

  private onSpecificMenuLoad(
    ctx: StateContext<MenuTreeStructureStateModel>,
    displayName: string | undefined,
    nodeId: string | undefined,
    module: number
  ) {
    return (menuStructure: MenuItemModel[]) => {
      ctx.setState(
        patch({
          displayName,
          currentModule: module,
          displayedTreeModule: module,
        })
      );
      if (nodeId) {
        this.updateStateWithSelectedMenu(menuStructure, nodeId, ctx, displayName);
      } else {
        this.updateStateWhenNoIdWasSelected(ctx, menuStructure, module);
      }
      ctx.dispatch(new SaveMenuStructureIdNameList(menuStructure));
    };
  }

  private updateStateWhenNoIdWasSelected(
    ctx: StateContext<MenuTreeStructureStateModel>,
    menuStructure: MenuItemModel[],
    module: number
  ) {
    const state = ctx.getState();
    const menuIdBelongsToCurrentStructure =
      !!state.currentTableData[0] &&
      !!this.createUpdateDeleteRecursiveArrayService.getNodeWithChildrenOneLevelDeep(
        menuStructure,
        state.currentTableData[0]?.id
      );
    ctx.setState({
      ...state,
      loaded: true,
      menuStructure,
      currentTableData: menuIdBelongsToCurrentStructure ? state.currentTableData : [],
      currentModule: module,
      displayedTreeModule: module,
    });
  }

  private updateStateWithSelectedMenu(
    menuStructure: MenuItemModel[],
    nodeId: string,
    ctx: StateContext<MenuTreeStructureStateModel>,
    displayName: string | undefined
  ) {
    const nodeWithChildren =
      this.createUpdateDeleteRecursiveArrayService.getNodeWithChildrenOneLevelDeep(
        menuStructure,
        nodeId
      );
    ctx.setState(
      patch({
        loaded: true,
        menuStructure,
        currentTableData: [nodeWithChildren],
        displayName: displayName ?? '',
        activeMenuElementId: nodeId,
      })
    );
  }

  static getOpenedMenuItemIdsForModule(module: number) {
    return createSelector([MenuTreeStructureState], (state: MenuTreeStructureStateModel) => {
      return state.openedMenuItemIdsByModule[module] || [];
    });
  }
}
